// @ts-check
// Ações em árvore (contrato 08, seção 3.9): o passo IF_ELSE guarda os ramos "sim" e "não", cada um com sua lista de
// ações. A execução trabalha com a lista achatada em pré-ordem (o se/senão, depois o ramo sim, depois o não, depois o
// que vem em seguida), alinhada com os passos da execução; o ramo que não foi escolhido fica SKIPPED.

/**
 * @typedef {{ id: string, type: string, [key: string]: any }} AutomationAction
 * @typedef {{ action: AutomationAction, parentId: string | null, branch: 'yes' | 'no' | null, depth: number }} FlatStep
 */

export const BRANCH_KEYS = /** @type {const} */ (['yes', 'no']);

/**
 * @param {AutomationAction[]} actions @param {string | null} [parentId] @param {'yes' | 'no' | null} [branch] @param {number} [depth]
 * @returns {FlatStep[]}
 */
export function flattenActions(actions, parentId = null, branch = null, depth = 0) {
  /** @type {FlatStep[]} */
  const steps = [];
  for (const action of actions ?? []) {
    steps.push({ action, parentId, branch, depth });
    if (action.type === 'IF_ELSE') {
      for (const key of BRANCH_KEYS) steps.push(...flattenActions(action.branches?.[key] ?? [], action.id, key, depth + 1));
    }
  }
  return steps;
}

/**
 * Índices dos passos que ficam dentro de um ramo do se/senão (inclusive os aninhados).
 * @param {FlatStep[]} flat @param {string} ifElseId @param {'yes' | 'no'} branch
 */
export function branchStepIndexes(flat, ifElseId, branch) {
  const parentById = new Map(flat.map((step) => [step.action.id, step]));
  /** @param {FlatStep} step */
  const isInside = (step) => {
    let current = step;
    while (current.parentId) {
      if (current.parentId === ifElseId) return current.branch === branch;
      const parent = parentById.get(current.parentId);
      if (!parent) return false;
      current = parent;
    }
    return false;
  };
  return flat.flatMap((step, index) => (isInside(step) ? [index] : []));
}

/**
 * Passos que um passo pode usar como variável: os anteriores na ordem que não estão num ramo irmão (o outro ramo
 * de um se/senão do caminho até ele, que não roda na mesma execução).
 * @param {FlatStep[]} flat @param {number} index
 */
export function previousStepIndexes(flat, index) {
  const parentById = new Map(flat.map((step) => [step.action.id, step]));
  /** @param {FlatStep} step @returns {Map<string, string>} se/senão do caminho e o ramo seguido */
  const pathOf = (step) => {
    const path = new Map();
    let current = step;
    while (current.parentId) {
      path.set(current.parentId, current.branch);
      const parent = parentById.get(current.parentId);
      if (!parent) break;
      current = parent;
    }
    return path;
  };
  const targetPath = pathOf(flat[index]);
  return flat.slice(0, index).flatMap((step, position) => {
    const stepPath = pathOf(step);
    for (const [ifElseId, branch] of stepPath) {
      if (targetPath.has(ifElseId) && targetPath.get(ifElseId) !== branch) return [];
      // passo dentro de um se/senão que não está no caminho: pode não ter rodado, mas a variável fica vazia sem erro
    }
    return [position];
  });
}

/** Ações de uma árvore, em pré-ordem. @param {AutomationAction[]} actions */
export const allActions = (actions) => flattenActions(actions).map((step) => step.action);
